import type { ReactNode } from 'react';
import { Alert, Badge, TBody, THead, Table, Td, Th, Tr } from '@/components/ui';
import {
  PLACEHOLDER,
  formatAzimuth,
  formatConfidence,
  formatDMS,
  formatFixed,
  formatScale,
} from '@/logic/statistics';
import type {
  DataSnoopingResult,
  DataSnoopingResults,
  PointPair,
  TransformationSolution,
} from '@/types';
import { largestActiveResidual, pairLabelOf } from './adjustmentDisplay';
import { RejectionAction } from './RejectionAction';
import { MathSymbol, SigmaZero, Tau } from './Symbols';

/** The pair each iteration tested: the rejected one, else the largest |τ|. */
const testedPair = (iteration: DataSnoopingResult) => {
  const id =
    iteration.rejectedPointId ?? iteration.maxStandardizedResidualPointId;
  const residual = iteration.solution.residuals.find((r) => r.id === id);
  return {
    id,
    tau: iteration.standardizedResidual ?? iteration.maxStandardizedResidual,
    redundancyNumber: iteration.redundancyNumber ?? residual?.redundancyNumber,
    mdb: iteration.minimumDetectableBias ?? residual?.minimumDetectableBias,
  };
};

const SummaryItem = ({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) => (
  <div className="min-w-0">
    <dt className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
      {label}
    </dt>
    <dd className="mt-0.5 text-sm text-fg">{children}</dd>
  </div>
);

const Mono = ({ children }: { children: ReactNode }) => (
  <span className="font-mono tabular-nums">{children}</span>
);

const comparisonRows = (
  initial: TransformationSolution,
  final: TransformationSolution
): Array<{ key: string; label: ReactNode; before: string; after: string }> => {
  const statistic = (solution: TransformationSolution, value?: number) =>
    solution.redundancy > 0 ? (value ?? null) : null;
  const largest = (solution: TransformationSolution) =>
    statistic(solution, largestActiveResidual(solution)?.total ?? undefined);
  return [
    {
      key: 'largest',
      label: 'Largest residual in fit',
      before: formatFixed(largest(initial), 3),
      after: formatFixed(largest(final), 3),
    },
    {
      key: 'rms',
      label: 'RMS',
      before: formatFixed(statistic(initial, initial.rmsTotal), 4),
      after: formatFixed(statistic(final, final.rmsTotal), 4),
    },
    {
      key: 'sigma0',
      label: (
        <>
          Unit weight <SigmaZero />
        </>
      ),
      before: formatFixed(initial.sigma0, 4),
      after: formatFixed(final.sigma0, 4),
    },
    {
      key: 'rotation',
      label: 'Rotation',
      before: formatDMS(initial.rotation),
      after: formatDMS(final.rotation),
    },
    {
      key: 'scale',
      label: 'Scale',
      before: formatScale(initial.scale),
      after: formatScale(final.scale),
    },
    {
      key: 'pairs',
      label: 'Pairs in fit · redundancy',
      before: `${initial.pointCount} · r ${initial.redundancy}`,
      after: `${final.pointCount} · r ${final.redundancy}`,
    },
  ];
};

/** Iterative data snooping (Pope's τ test) results and recommendation. */
export const DataSnoopingView = ({
  results,
  pointPairs,
  previewBlockedReason,
  onPreview,
}: {
  results: DataSnoopingResults;
  pointPairs: PointPair[];
  /** Why the recommendation cannot be previewed (outdated results). */
  previewBlockedReason?: string;
  onPreview: (rejectedIds: number[]) => void;
}) => {
  const {
    iterations,
    finalRejectedIds,
    alpha,
    finalSolution,
    initialSolution,
  } = results;
  // Results from before the τ test labeled the same statistic as a w-test.
  const isTau = results.testStatistic === 'tau';
  const statistic = isTau ? <Tau /> : <MathSymbol>w</MathSymbol>;
  const rejectedLabels = finalRejectedIds.map((id) =>
    pairLabelOf(pointPairs, id)
  );
  const firstRedundancy = results.redundancy ?? iterations[0]?.redundancy;
  const last = iterations[iterations.length - 1];

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-border bg-surface-sunken/60 px-4 py-3 sm:grid-cols-4">
        <SummaryItem label="Confidence">
          <Mono>{formatConfidence(alpha)}</Mono>{' '}
          <span className="text-xs text-fg-muted">α = {alpha}</span>
        </SummaryItem>
        <SummaryItem label={<>{statistic} critical, first test</>}>
          <Mono>
            {results.criticalValue > 0
              ? formatFixed(results.criticalValue, 2)
              : PLACEHOLDER}
          </Mono>
        </SummaryItem>
        <SummaryItem label="Redundancy">
          <Mono>
            {firstRedundancy !== undefined
              ? `r = ${firstRedundancy}`
              : PLACEHOLDER}
          </Mono>
        </SummaryItem>
        <SummaryItem label="Outcome">
          {finalRejectedIds.length > 0 ? (
            <span className="font-medium text-danger-fg">
              Rejects <Mono>{rejectedLabels.join(', ')}</Mono>
            </span>
          ) : results.stopReason === 'noTestableObservations' ? (
            'Nothing testable'
          ) : (
            <span className="font-medium text-success-fg">All pairs pass</span>
          )}
        </SummaryItem>
      </dl>

      {!isTau && (
        <Alert tone="warning" title="Earlier test version">
          These results used normal critical values and were labeled a w-test.
          Re-run the check for the τ test.
        </Alert>
      )}
      {results.insufficientRedundancy ? (
        <Alert tone="warning" title="Testing stopped early">
          Redundancy fell below 2 after rejections, so the remaining pairs were
          not tested. Add control before treating the data as clean.
        </Alert>
      ) : results.lowPower ? (
        <Alert tone="warning" title="Low power">
          {last?.redundancy !== undefined
            ? `With r = ${last.redundancy}, `
            : 'At this redundancy, '}
          a blunder near the minimal detectable bias is more likely to pass than
          to be flagged
          {finalRejectedIds.length > 0 ? '; smaller blunders may remain' : ''}.
          A lower confidence level or more control strengthens the test.
        </Alert>
      ) : null}
      {results.stopReason === 'noTestableObservations' && (
        <Alert>
          No residual component could be tested: the fit is exact or its
          redundancy numbers are zero.
        </Alert>
      )}

      {iterations.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table
            aria-label="Data snooping iterations"
            className="[&_td]:whitespace-nowrap"
          >
            <THead>
              <Tr>
                <Th numeric className="w-0">
                  #
                </Th>
                <Th>
                  <abbr
                    title="The rejected pair, or the pair with the largest test statistic when all pass"
                    className="no-underline"
                  >
                    Tested pair
                  </abbr>
                </Th>
                <Th numeric>|{statistic}|</Th>
                <Th numeric>{statistic} crit</Th>
                <Th numeric>
                  <abbr
                    title="Degrees of freedom of this fit"
                    className="no-underline"
                  >
                    <MathSymbol>r</MathSymbol>
                  </abbr>
                </Th>
                <Th numeric>
                  <abbr
                    title="Redundancy number of the tested component"
                    className="no-underline"
                  >
                    <MathSymbol>rᵢ</MathSymbol>
                  </abbr>
                </Th>
                <Th numeric>
                  <abbr
                    title="Minimal detectable bias at 80% power"
                    className="no-underline"
                  >
                    MDB
                  </abbr>
                </Th>
                <Th numeric>Max residual</Th>
                <Th>At pair</Th>
                <Th>Result</Th>
              </Tr>
            </THead>
            <TBody className="[&>tr:last-child>th]:border-b-0">
              {iterations.map((iteration) => {
                const tested = testedPair(iteration);
                return (
                  <Tr key={iteration.iteration}>
                    <Td numeric className="text-fg-muted">
                      {iteration.iteration}
                    </Td>
                    <Td
                      mono
                      className={
                        iteration.rejectedPointId !== null
                          ? 'font-medium'
                          : 'text-fg-muted'
                      }
                    >
                      {iteration.rejectedLabel ??
                        (tested.id !== undefined
                          ? pairLabelOf(pointPairs, tested.id)
                          : PLACEHOLDER)}
                    </Td>
                    <Td numeric>{formatFixed(tested.tau, 2)}</Td>
                    <Td numeric className="text-fg-muted">
                      {formatFixed(iteration.criticalValue, 2)}
                    </Td>
                    <Td numeric className="text-fg-muted">
                      {iteration.redundancy ?? PLACEHOLDER}
                    </Td>
                    <Td numeric className="text-fg-muted">
                      {formatFixed(tested.redundancyNumber, 2)}
                    </Td>
                    <Td numeric className="text-fg-muted">
                      {formatFixed(tested.mdb, 3)}
                    </Td>
                    <Td numeric>{formatFixed(iteration.maxResidual, 3)}</Td>
                    <Td>
                      <span className="inline-flex items-baseline gap-1.5">
                        <span className="font-mono">
                          {iteration.maxResidualLabel ?? PLACEHOLDER}
                        </span>
                        <span className="text-xs text-fg-muted">
                          {formatAzimuth(iteration.maxResidualAzimuth)}
                        </span>
                      </span>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1">
                        {iteration.passed ? (
                          <Badge tone="success" dot>
                            Pass
                          </Badge>
                        ) : (
                          <Badge tone="danger" dot>
                            Reject
                          </Badge>
                        )}
                        {iteration.lowPower && (
                          <Badge tone="warning">Low power</Badge>
                        )}
                      </span>
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        </div>
      )}

      {initialSolution && finalSolution && finalRejectedIds.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table aria-label="Before and after data snooping">
            <THead>
              <Tr>
                <Th>Measure</Th>
                <Th numeric>Initial</Th>
                <Th numeric>After rejections</Th>
              </Tr>
            </THead>
            <TBody className="[&>tr:last-child>th]:border-b-0">
              {comparisonRows(initialSolution, finalSolution).map((row) => (
                <Tr key={row.key}>
                  <Th
                    scope="row"
                    className="h-9 bg-transparent text-sm font-normal normal-case tracking-normal text-fg-muted"
                  >
                    {row.label}
                  </Th>
                  <Td numeric className="h-9 text-fg-muted">
                    {row.before}
                  </Td>
                  <Td numeric className="h-9">
                    {row.after}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      {finalRejectedIds.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-fg-muted">
            Recommendation: set{' '}
            <span className="font-mono text-fg">
              {rejectedLabels.join(', ')}
            </span>{' '}
            to Free.
          </p>
          <RejectionAction
            ids={finalRejectedIds}
            pointPairs={pointPairs}
            onPreview={onPreview}
            disabledReason={previewBlockedReason}
          />
        </div>
      )}
    </div>
  );
};
